import {
  ArrayInput,
  BooleanInput,
  Create,
  Edit,
  NumberInput,
  required,
  Resource,
  SimpleFormIterator,
  TextInput,
  useRedirect,
} from "react-admin";
import { BackofficeRecordTitle } from "../misc/BackofficeRecordTitle";
import { useAutovioContext } from "../hooks/useAutovioContext";
import { Column } from "../misc/Column";
import Box from "@mui/material/Box";
import { useRecordAndSwitchDrivingSchoolIfNecessary } from "../hooks/useRecordAndSwitchDrivingSchoolIfNecessary";
import { LoadingIndicator } from "../misc/LoadingIndicator";
import { overviewCardStyle } from "../misc/backofficeTheme";
import { Row } from "../misc/Row";
import { Form2 } from "../misc/Form2";
import { grants } from "../backoffice.access_control";
import { AccessDenied } from "../misc/AccessDenied";
import { type SubscriptionPackage } from "../providers/subscriptionPackagesProvider";
import { SaveButton2 } from "../buttons/SaveButton2";
import { DeleteRecordButton } from "../buttons/DeleteRecordButton";
import { DefaultList } from "../misc/DefaultList";
import { RecordDetailsHeader } from "../misc/RecordDetailsHeader";

function CreateSubscriptionPackageForm() {
  const [{ drivingSchoolId }] = useAutovioContext();
  const redirect = useRedirect();

  if (!grants.has("SubscriptionPackage:create")) {
    return <AccessDenied />;
  }

  if (!drivingSchoolId) {
    return (
      <Box className="centered">
        <Column sx={{ alignItems: "center" }}>Bitte wähle eine Fahrschule aus.</Column>
      </Box>
    );
  }

  return (
    <Create
      title={<BackofficeRecordTitle />}
      mutationOptions={{
        onSuccess: () => {
          showSuccessToast("AUTOVIO Pro Paket erfolgreich erstellt.");
          redirect(`/drivingSchools/${drivingSchoolId}/subscriptionPackages`);
        },
        onError: (error: Error) => {
          handleError("Fehler beim Erstellen des AUTOVIO Pro Pakets.", error);
        },
      }}
      transform={(data: Omit<SubscriptionPackage, "drivingSchoolId">): SubscriptionPackage => ({
        ...data,
        drivingSchoolId,
        price: { amount: data.price?.amount ?? 0, currency: "EUR" },
      })}
    >
      <SubscriptionPackageForm mode="create" />
    </Create>
  );
}

function EditSubscriptionPackageForm() {
  const subscriptionPackage = useRecordAndSwitchDrivingSchoolIfNecessary<SubscriptionPackage>(
    (it) => it.drivingSchoolId,
  );
  const redirect = useRedirect();

  if (!grants.has("SubscriptionPackage:list")) {
    return <AccessDenied />;
  }

  if (!subscriptionPackage) {
    return <LoadingIndicator />;
  }

  return (
    <Edit
      title={
        <BackofficeRecordTitle
          header={
            <RecordDetailsHeader
              backLabel="AUTOVIO Pro"
              backUrl={`/drivingSchools/${subscriptionPackage.drivingSchoolId}/subscriptionPackages`}
              title={subscriptionPackage.name}
            />
          }
        />
      }
      mutationMode="pessimistic"
      mutationOptions={{
        onSuccess: () => {
          showSuccessToast("Änderungen erfolgreich gespeichert.");
          redirect(`/drivingSchools/${subscriptionPackage.drivingSchoolId}/subscriptionPackages`);
        },
        onError: (error: unknown) => {
          handleError("Fehler beim Speichern.", error);
        },
      }}
      transform={(data: SubscriptionPackage): SubscriptionPackage => ({
        ...data,
        price: { amount: data.price?.amount ?? 0, currency: "EUR" },
      })}
    >
      <SubscriptionPackageForm mode="edit" />
    </Edit>
  );
}

function SubscriptionPackageForm(props: { mode: "create" | "edit" }) {
  const { mode } = props;
  const [{ drivingSchoolId }] = useAutovioContext();
  return (
    <Form2 warnWhenUnsavedChanges>
      <Column sx={{ padding: overviewCardStyle.padding }}>
        <TextInput
          label="Name"
          source="name"
          autoFocus={mode === "create"}
          validate={required("Bitte gib den Namen des AUTOVIO Pro Pakets ein.")}
        />
        <NumberInput
          label="Preis (€)"
          source="price.amount"
          min={0}
          step={0.01}
          validate={required("Bitte gib den Preis ein.")}
        />
        <BooleanInput label="Empfohlen" source="recommended" defaultValue={false} />
        <ArrayInput label="Merkmale" source="features" data-testid="subscriptionPackage-features">
          <SimpleFormIterator>
            <TextInput
              label="Merkmal"
              source=""
              InputLabelProps={{ sx: { lineHeight: "unset" } }}
              helperText={false}
              validate={required("Bitte gib ein mindestens ein.")}
            />
          </SimpleFormIterator>
        </ArrayInput>
        <Row sx={{ pt: 1, justifyContent: "space-between" }}>
          {(mode === "create" ? grants.has("SubscriptionPackage:create") : grants.has("SubscriptionPackage:edit")) && (
            <SaveButton2 />
          )}
          {mode === "edit" && grants.has("SubscriptionPackage:delete") && (
            <DeleteRecordButton onSuccessRedirectTo={`/drivingSchools/${drivingSchoolId}/subscriptionPackages`} />
          )}
        </Row>
      </Column>
    </Form2>
  );
}

export const subscriptionPackagesResource = (
  <Resource
    key="subscriptionPackages"
    name="subscriptionPackages"
    list={DefaultList}
    create={CreateSubscriptionPackageForm}
    edit={EditSubscriptionPackageForm}
    options={{ label: "AUTOVIO Pro", createTitle: "Neues AUTOVIO Pro Paket" }}
    recordRepresentation={(p: SubscriptionPackage) => p.name}
  />
);
